import { GAME_NAME, WHEEL_MATCH_OFFERED } from '../constants';
import type { GameState } from '../game/state';
import { el } from '../lib/dom';
import { concede, gameStore } from '../state/game';
import { localSide } from '../state/localSide';
import { logOpenStore, toggleLog } from '../state/logOpen';
import { startMatch, startNewMatch } from '../state/newMatch';
import { roomStore } from '../state/roomStore';
import {
  randomSeed,
  type Settings,
  setMode,
  setSeed,
  setSound,
  setSquareNames,
  settingsStore,
} from '../state/settings';
import { isSettingUp, subscribeSetup } from '../state/setup';
import { createAbout } from './About';
import { createAskingButton } from './AskingButton';
import { createLogoMark } from './LogoMark';
import { createMenu } from './Menu';
import { createRestartButton } from './RestartButton';
import { createRoomBar } from './RoomBar';
import { createRoomMenu } from './RoomMenu';
import { createSettingSwitch } from './SettingSwitch';
import { createThemePicker } from './ThemePicker';
import { createTopBarIcon } from './topBarIcons';

/** The settings a player changes once a match, or never, are behind this. */
const MENU_LABEL = 'Menu';
/** Godsmode: shows and hides the log. A plain word, not final copy. */
const LOG_LABEL = 'Log';
const ABOUT_LABEL = 'About';
const NEW_MATCH_LABEL = 'New match';
/** Plain words, not final copy. */
const SOUND_LABEL = 'Sound';
const SQUARE_NAMES_LABEL = 'Square names';
/** Rob's name for the mode. The switch sets the mode of the next match. */
const GODSMODE_LABEL = 'Godsmode';
/** Rob's word, 2026-10-15. The player gives up, its royal pieces explode, and the other side wins. */
const SURRENDER_LABEL = 'Surrender';

// The turn number is in the corner of the cue since 2026-10-14, and the top bar has no chip.
export const createTopBar = (): HTMLElement => {
  const seedInput = el('input', {
    className: 'setting__input',
    type: 'number',
    min: '1',
    inputMode: 'numeric',
    onchange: () => setSeed(Number(seedInput.value) || randomSeed()),
  });

  // New match is in the menu, and it asks once when a match is in progress. A second
  // click while the question shows starts the match.
  const newButton = createAskingButton({
    label: NEW_MATCH_LABEL,
    mustAsk: () => {
      const { ply, phase } = gameStore.get();
      return ply > 0 && phase !== 'over';
    },
    onConfirm: startNewMatch,
  });

  // The player gives up, and the other side wins. It always asks. A match that is over has no such button.
  // It is a key of the top bar since 2026-10-15, beside Log. It was in the menu. Rob: "We need an easy
  // \"surrender\" option".
  const concedeButton = createAskingButton({
    label: SURRENDER_LABEL,
    className: 'topbar__surrender',
    mustAsk: () => true,
    onConfirm: () => concede(localSide()),
    content: () => [
      createTopBarIcon('surrender'),
      el('span', { className: 'button__label', textContent: SURRENDER_LABEL }),
    ],
  });
  concedeButton.ariaLabel = SURRENDER_LABEL;

  const restartButton = createRestartButton(startMatch);

  const about = createAbout();
  const aboutButton = el('button', {
    className: 'button button--pass-through',
    type: 'button',
    textContent: ABOUT_LABEL,
    onclick: about.open,
  });

  const soundSwitch = createSettingSwitch(SOUND_LABEL, setSound);
  const squareNamesSwitch = createSettingSwitch(SQUARE_NAMES_LABEL, setSquareNames);
  const godsmodeSwitch = createSettingSwitch(GODSMODE_LABEL, (on) =>
    setMode(on ? 'gods' : 'classic'),
  );

  const drawSettings = (settings: Settings): void => {
    soundSwitch.draw(settings.sound);
    squareNamesSwitch.draw(settings.squareNames);
    godsmodeSwitch.draw(settings.mode === 'gods');
    godsmodeSwitch.root.hidden = !WHEEL_MATCH_OFFERED;
    seedInput.value = String(settings.seed);
  };

  const drawGame = (state: GameState): void => {
    concedeButton.hidden = state.phase === 'over';
  };

  drawSettings(settingsStore.get());
  settingsStore.subscribe(drawSettings);
  drawGame(gameStore.get());
  gameStore.subscribe(drawGame);

  // Godsmode: the log is behind this button. The styles hide it in a match with a wheel.
  const logButton = el(
    'button',
    {
      className: 'button button--pass-through topbar__log',
      type: 'button',
      ariaLabel: LOG_LABEL,
      onclick: toggleLog,
    },
    [createTopBarIcon('log'), el('span', { className: 'button__label', textContent: LOG_LABEL })],
  );
  const drawLogButton = (open: boolean): void => {
    logButton.ariaPressed = String(open);
  };
  drawLogButton(logOpenStore.get());
  logOpenStore.subscribe(drawLogButton);

  // In a room the match is the room's. New match, the restart, and the seed are for a match against the AI.
  const matchRow = el('div', { className: 'menu__match' }, [newButton, restartButton]);
  const seedRow = el('label', { className: 'setting' }, [
    el('span', { className: 'setting__label', textContent: 'Seed' }),
    seedInput,
  ]);
  const drawRoom = (): void => {
    const inRoom = roomStore.get() !== null;
    matchRow.hidden = inRoom;
    seedRow.hidden = inRoom;
  };
  drawRoom();
  roomStore.subscribe(drawRoom);

  return el('header', { className: 'topbar' }, [
    el('div', { className: 'topbar__brand' }, [
      el('h1', { className: 'topbar__title' }, [createLogoMark(), GAME_NAME]),
      createRoomBar(),
    ]),
    el('div', { className: 'topbar__controls' }, [
      concedeButton,
      logButton,
      createMenu(
        MENU_LABEL,
        [
          createRoomMenu(),
          matchRow,
          seedRow,
          godsmodeSwitch.root,
          el('div', { className: 'setting setting--inline' }, [
            el('span', { className: 'setting__label', textContent: 'Look' }),
            createThemePicker(),
          ]),
          soundSwitch.root,
          squareNamesSwitch.root,
          aboutButton,
        ],
        createTopBarIcon('menu'),
        // New match covers the board. The panel goes, so it does not lie over the cover.
        (close) =>
          subscribeSetup(() => {
            if (isSettingUp()) close();
          }),
      ),
      about.root,
    ]),
  ]);
};
